<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppCoachmark from './AppCoachmark/AppCoachmark.vue'
import { useOptionalChatDock } from '../chat/AppChatPanel/useChatDock'
import { useSectionTour } from './useSectionTour'
import { useArrivalStore } from '@/stores/arrival'
import { useAuthStore } from '@/stores/auth'
import { useSetupStore } from '@/stores/setup'
import { useTourStore } from '@/stores/tour'

const { t } = useI18n()
const tour = useTourStore()
const arrival = useArrivalStore()
const auth = useAuthStore()
const setup = useSetupStore()

useSectionTour()

const chatDock = useOptionalChatDock()
if (chatDock) watch(chatDock.open, (open) => tour.setDockOpen(open), { immediate: true })

const step = computed(() => tour.activeStep)

const nextLabel = computed(() => {
  const current = step.value
  if (!current) return undefined
  if (!current.isLast) return t('tour.controls.next')
  return current.count === 1 ? t('tour.controls.gotIt') : t('tour.controls.done')
})

const backLabel = computed(() =>
  step.value && !step.value.isFirst ? t('tour.controls.back') : undefined,
)

const skipLabel = computed(() =>
  step.value && !step.value.isLast ? t('tour.controls.skip') : undefined,
)

const isSeries = computed(() => (step.value?.count ?? 0) > 1)

const stepLabel = computed(() =>
  step.value && isSeries.value
    ? t('tour.controls.step', { index: step.value.index + 1, total: step.value.count })
    : undefined,
)

const offeringHandoff = computed(
  () =>
    step.value === null &&
    arrival.scene === null &&
    setup.resolved &&
    auth.hasWorkspace &&
    !setup.needsSetup &&
    tour.canOfferHandoff,
)

const handoffTarget = computed(() =>
  offeringHandoff.value ? tour.targetFor('shell.tourHub') : null,
)

watch(
  () => step.value?.target ?? null,
  (target) => {
    target?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  },
  { flush: 'post' },
)
</script>

<template>
  <Teleport v-if="step" to="body">
    <AppCoachmark
      :target="step.target"
      :placement="step.placement"
      :variant="step.variant"
      :title="t(step.titleKey)"
      :body="t(step.bodyKey)"
      :step-index="isSeries ? step.index : undefined"
      :step-count="isSeries ? step.count : undefined"
      :step-label="stepLabel"
      :next-label="nextLabel"
      :back-label="backLabel"
      :skip-label="skipLabel"
      :close-label="t('tour.controls.close')"
      @next="tour.next()"
      @back="tour.back()"
      @skip="tour.skip()"
      @close="tour.skip()"
    />
  </Teleport>

  <Teleport v-else-if="handoffTarget" to="body">
    <AppCoachmark
      :target="handoffTarget"
      placement="right-end"
      variant="soft"
      :title="t('tour.handoff.title')"
      :body="t('tour.handoff.body')"
      :next-label="t('tour.handoff.accept')"
      :skip-label="t('tour.handoff.decline')"
      :close-label="t('tour.controls.close')"
      @next="tour.acceptHandoff()"
      @skip="tour.declineHandoff()"
      @close="tour.declineHandoff()"
    />
  </Teleport>
</template>
